<template>
	<div class="ventas_tab">
		<!-- ================================================================ -->
		<!-- Header Section                                                    -->
		<!-- ================================================================ -->
		<div
			class="header_section_header is-flex is-flex-direction-row is-justify-content-space-between is-align-items-end mb-5">
			<div>
				<div class="header_title">Ventas</div>
				<div class="header_description mb-4">
					Analiza la tendencia y composición de tus ventas
				</div>

				<!-- Sub-Navigation: rango de períodos -->
				<div class="sub_nav_filters">
					<button v-for="filter in getFiltersSubNav" :key="filter.id" class="filter_btn"
						:class="{ 'is-active': activeSubFilter === filter.id }"
						@click="updateActiveSubFilter(filter.id)">
						{{ filter.label }}
					</button>
				</div>
			</div>

			<!-- Right Actions -->
			<div class="right_actions is-flex">
				<div class="calendar_picker is-flex is-align-items-center mr-3">
					<AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth"
						:disableNewerDates="true" :disabledFrom="getNow" formatLabelDate="MMMM YYYY" :alignRight="true" />
				</div>
				<button class="theme_btn_white is-flex is-align-items-center">
					<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
					Exportar
				</button>
			</div>
		</div>

		<!-- Summary Cards Section -->
		<div class="cards_container mt-5 is-flex">
			<CardVentas v-for="(card, index) in dynamicSummaryCards" :key="index" v-bind="card" class="mr-4" />
		</div>

		<!-- Main Table Section -->
		<div class="mt-6 p-5 container_section">
			<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
				<div class="is-flex is-align-items-center">
					<Ecs class="mr-2" style="width: 20px; height: 20px;" />
					<span class="tittle_section">Evolución de Ventas Netas</span>
				</div>
				<div class="table_actions is-flex">
					<button class="action_btn mr-2">
						<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
						Comparación
					</button>
					<button class="action_btn mr-2">
						<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
						Ver Gráfico
					</button>
					<div style="min-width: 140px;">
						<SelectComponent v-model="selectedTablePeriod" theme="report_theme" :options="periodOptions" />
					</div>
				</div>
			</div>

			<section>
				<!-- Loading state -->
				<div v-if="isLoading" class="loading_state py-6 has-text-centered">
					<p>Cargando datos...</p>
				</div>

				<!-- Error state -->
				<div v-else-if="error" class="error_state py-6 has-text-centered">
					<p class="has-text-danger">{{ error }}</p>
					<button class="action_btn mt-3" @click="fetchSalesData">Reintentar</button>
				</div>

				<!-- Empty state -->
				<div v-else-if="!isLoading && monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
					<p>No hay datos de ventas para el período seleccionado.</p>
				</div>

				<!-- Table -->
				<div v-else class="table_container" ref="mainTable">
					<table class="custom_table">
						<colgroup>
							<col style="width: 200px;" />
							<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
						</colgroup>
						<thead>
							<tr>
								<th scope="col" class="sticky-column header_label nobreak"></th>
								<th scope="col" class="has-text-right nobreak is-capitalized"
									v-for="header in monthHeaders" :key="header">
									{{ header }}
								</th>
							</tr>
						</thead>
						<tbody>
							<!-- Filas de cuentas level_3 -->
							<tr v-for="row in paginatedTableRows" :key="row.label" class="theme_row_white">
								<th scope="row" class="sticky-column nobreak theme_row_white is-capitalized">
									<span v-if="row.label.length > 30" 
										class="has-tooltip-right" 
										:data-tooltip="row.label">
										{{ row.label.substring(0, 30) + '...' }}
									</span>
									<span v-else>
										{{ row.label }}
									</span>
								</th>
								<td v-for="(val, idx) in row.values" :key="idx"
									class="has-text-right nobreak theme_row_white">
									{{ money(val) }}
								</td>
							</tr>

							<!-- Fila de totales -->
							<tr class="theme_row_azure">
								<th scope="row" class="sticky-column nobreak theme_row_azure">
									{{ totalRow.label }}
								</th>
								<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
									class="has-text-right nobreak theme_row_azure">
									{{ money(val) }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="mt-4 pb-2" v-if="tableRows.length > itemsPerPage">
					<Pagination v-model="currentPage" :maxPages="maxPages" />
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { parseToMoney } from "@/utils/currency";
import AnualCalendar from "@/components/classification/AnualCalendar";
import SelectComponent from "@/components/classification/SelectComponent";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import Ecs from "@/components/svg/Ecs.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import { PERIOD_TYPES } from "../config/reportStructure";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import Pagination from "@/components/generics/Pagination.vue";

export default {
	name: "VentasTab",

	components: {
		AnualCalendar,
		SelectComponent,
		DownloadFile,
		Ecs,
		ChartLine,
		CardVentas,
		Pagination
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			selectedTablePeriod: 'lastYear',
			currentPage: 1,
			itemsPerPage: 10,
			summaryCards: [
				{
					period: 'Abr 2025',
					amount: 201000000,
					mom: '8.56%',
					yoy: '15.3%',
					progressItems: [
						{ label: 'Productos', percent: 62 },
						{ label: 'Despachos A Clientes', percent: 38 }
					]
				},
				{
					period: 'Ene - Abr 2025',
					amount: 750000000,
					yoy: '5.12%',
					progressItems: [
						{ label: 'Productos', percent: 58 },
						{ label: 'Despachos A Clientes', percent: 42 }
					]
				},
				{
					period: 'May 2024 - Abr 2025',
					amount: 2400000000,
					yoy: '18.7%',
					progressItems: [
						{ label: 'Productos', percent: 60 },
						{ label: 'Despachos A Clientes', percent: 40 }
					]
				}
			]
		};
	},

	computed: {
		...mapState('classification', ['categories']),
		...mapState("salesReport", [
			"endMonth",
			"selectedPeriodRange",
			"activeSubFilter",
			"monthHeaders",
			"accounts",
			"accountsData",
			"periodRange",
			"isLoading",
			"error",
		]),
		...mapGetters("salesReport", ["tableRows", "totalRow"]),
		getNow() {
			return moment().toDate();
		},
		getFiltersSubNav() {
			const filters = [{ id: 'cuentas', label: 'Cuentas' }]
			this.categories.forEach(category => {
				filters.push({
					id: category,
					label: category.charAt(0).toUpperCase() + category.slice(1)
				})
			})
			filters.push({ id: 'clientes', label: 'Clientes' })

			return filters
		},
		maxPages() {
			return Math.ceil(this.tableRows.length / this.itemsPerPage);
		},
		paginatedTableRows() {
			const start = (this.currentPage - 1) * this.itemsPerPage;
			const end = start + this.itemsPerPage;
			return this.tableRows.slice(start, end);
		},
		dynamicSummaryCards() {
			const cards = [...this.summaryCards];
			
			const lastIndex = this.monthHeaders.length - 1;
			if (lastIndex >= 0 && this.totalRow && this.tableRows.length > 0) {
				
				// --- Card 1: Último mes ---
				const currentIndices = [lastIndex];
				const currentAmount = this.getIndicesTotalAmount(currentIndices);
				cards[0] = {
					...cards[0],
					period: this.formatPeriodLabel(currentIndices),
					amount: currentAmount,
					progressItems: this.getIndicesProgressItems(currentIndices, currentAmount)
				};

				// --- Card 2: YTD ---
				const currentYear = this.monthHeaders[lastIndex].split(' ').pop();
				const ytdIndices = [];
				for (let i = 0; i <= lastIndex; i++) {
					if (this.monthHeaders[i].endsWith(currentYear)) {
						ytdIndices.push(i);
					}
				}
				const ytdAmount = this.getIndicesTotalAmount(ytdIndices);
				cards[1] = {
					...cards[1],
					period: this.formatPeriodLabel(ytdIndices),
					amount: ytdAmount,
					progressItems: this.getIndicesProgressItems(ytdIndices, ytdAmount)
				};

				// --- Card 3: Últimos 12 meses (LTM) ---
				const ltmIndices = [];
				for (let i = Math.max(0, lastIndex - 11); i <= lastIndex; i++) {
					ltmIndices.push(i);
				}
				const ltmAmount = this.getIndicesTotalAmount(ltmIndices);
				cards[2] = {
					...cards[2],
					period: this.formatPeriodLabel(ltmIndices),
					amount: ltmAmount,
					progressItems: this.getIndicesProgressItems(ltmIndices, ltmAmount)
				};

			} else {
				cards[0] = { ...cards[0], period: '-', amount: 0, progressItems: [] };
				cards[1] = { ...cards[1], period: '-', amount: 0, progressItems: [] };
				cards[2] = { ...cards[2], period: '-', amount: 0, progressItems: [] };
			}
			
			return cards;
		}
	},

	methods: {
		getIndicesTotalAmount(indices) {
			let total = 0;
			indices.forEach(idx => {
				total += this.totalRow.values[idx] || 0;
			});
			return total;
		},
		getIndicesProgressItems(indices, totalAmount) {
			return this.tableRows.map(row => {
				let rowTotal = 0;
				indices.forEach(idx => {
					rowTotal += row.values[idx] || 0;
				});
				const percent = totalAmount === 0 ? 0 : (rowTotal / totalAmount) * 100;
				return {
					label: row.label,
					percent: Math.round(percent)
				};
			}).sort((a, b) => b.percent - a.percent).slice(0, 4);
		},
		formatPeriodLabel(indices) {
			if (!indices.length) return '-';
			const firstStr = this.monthHeaders[indices[0]];
			const lastStr = this.monthHeaders[indices[indices.length - 1]];
			
			const firstMonth = firstStr.split(' ')[0];
			const firstYear = firstStr.split(' ').pop();
			const lastMonth = lastStr.split(' ')[0];
			const lastYear = lastStr.split(' ').pop();
			
			const capFirst = firstMonth.charAt(0).toUpperCase() + firstMonth.slice(1);
			const capLast = lastMonth.charAt(0).toUpperCase() + lastMonth.slice(1);
			
			if (firstMonth === lastMonth && firstYear === lastYear) {
				return `${capFirst} ${firstYear}`;
			} else {
				if (firstYear === lastYear) {
					return `${capFirst} - ${capLast} ${lastYear}`;
				}
				return `${capFirst} ${firstYear} - ${capLast} ${lastYear}`;
			}
		},

		...mapActions('classification', ['getCategories']),
		...mapActions("salesReport", ["fetchSalesData"]),
		...mapMutations("salesReport", ["SET_END_MONTH", "SET_SELECTED_PERIOD_RANGE", "SET_ACTIVE_SUB_FILTER"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},

		updatePeriodRange(range) {
			this.SET_SELECTED_PERIOD_RANGE(range);
		},

		updateActiveSubFilter(filter) {
			this.SET_ACTIVE_SUB_FILTER(filter);
		},

		money(value) {
			return parseToMoney(value);
		},
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) {
					table.scrollLeft = table.scrollWidth;
				}
			});
		},
		formatMoney(val) {
			return parseToMoney(val);
		}
	},

	mounted() {
		this.fetchSalesData();
	},

	watch: {
		endMonth() {
			this.fetchSalesData();
		},
		selectedPeriodRange() {
			this.fetchSalesData();
		},
		selectedTablePeriod(val) {
			this.updatePeriodRange(val);
		},
		activeSubFilter() {
			this.fetchSalesData();
		},
		tableRows() {
			this.currentPage = 1;
		},
		monthHeaders() {
			this.scrollToRight();
		},
		isLoading(val) {
			if (!val) {
				this.scrollToRight();
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.ventas_tab {
	position: relative;
	padding-bottom: 40px;
	overflow-y: auto;
	height: 100%;
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	color: #111827;
	margin-bottom: 4px;
}

.header_description {
	font-size: 16px;
	color: #6b7280;
}

// ------------------------------------------------------------------
// Sub-nav filters (rango de período)
// ------------------------------------------------------------------

.sub_nav_filters {
	display: flex;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: #4b5563;
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: #f9fafb;
	}

	&.is-active {
		background: #0d3c3f;
		color: #ffffff;
	}
}

// ------------------------------------------------------------------
// Right actions
// ------------------------------------------------------------------

.calendar_picker {
	background: #0d3c3f;
	color: white;
	border-radius: 8px;
	font-weight: 500;

	::v-deep .calendar-label {
		color: white !important;
	}
}

.theme_btn_white {
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover {
		background: #f9fafb;
	}
}

.action_btn {
	background: white;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: #4b5563;
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover {
		background: #f9fafb;
	}
}

// ------------------------------------------------------------------
// Container section
// ------------------------------------------------------------------

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
}

.container_section {
	background: white;
	border-radius: 16px;
	border: 1px solid #e5e7eb;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

// ------------------------------------------------------------------
// Table
// ------------------------------------------------------------------

.table_container {
	overflow-x: auto;
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	background: #ffffff;
	max-width: 100%;
}

.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid #e5e7eb;
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	td,
	th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid #f3f4f6;
		vertical-align: middle;
	}

	thead th,
	td {
		text-align: end;
	}

	thead th {
		padding: 12px 24px;
		color: #6b7280;
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: #f9fafb;
	}

	tbody th {
		color: #111827;
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: #4b5563;
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	.theme_row_white {
		background-color: white;
	}

	.header_label {
		background-color: #f9fafb;
	}

	.theme_row_azure {
		background-color: #ecfeff;
		font-weight: 600;
	}
}

// ------------------------------------------------------------------
// States
// ------------------------------------------------------------------

.loading_state,
.empty_state,
.error_state {
	color: #6b7280;
	font-size: 14px;
}

.nobreak {
	white-space: nowrap;
}
</style>
